// Turning a TEMPLATE into an instance: a URL value plus a dashboard's declared
// parameters becomes a `FilterState`.
//
// **What a `DashboardParameter` actually is.** A named URL value bound to one
// native filter (`{name, filter_id, default}`). It is deliberately not a query
// parameter and never reaches SQL as text: it seeds a SELECT filter's
// selection, and that selection binds as an ordinary `RuntimeFilterValue` — a
// bound parameter on the compiled query, same as one a reader clicked. That is
// the whole reason a template can take a string from an address bar at all
// while `DatasetParameter` stays numeric-only.
//
// **The failure this module is written against.** A parameter naming a filter
// the dashboard does not have, or a filter with no `target_column`, seeds
// nothing — and a dashboard that silently shows EVERY experiment when you asked
// for one experiment looks like it worked. So `applyParameters` reports what it
// could not bind, and the page says so instead of rendering an unfiltered board
// under a specific experiment's name.
import type { DashboardParameter, FilterState, NativeFilterSpec } from "@bi/shared/types";

import { defaultFilterState } from "./filter-state";

export interface TemplateInstance {
  filters: FilterState;
  /** Parameters that were given a value but could not be bound to a control.
   *  Named, not counted: the reader needs to know WHICH narrowing did not
   *  happen. */
  unbound: string[];
  /** Parameters the dashboard declares that the URL said nothing about and
   *  that carry no default. The board still renders; it is simply wider than a
   *  fully-specified instance. */
  unset: string[];
}

/**
 * Seed a template's controls from URL values.
 *
 * `values` is keyed by PARAMETER name (`{experiment: "budget_v3"}`). A value
 * present in `values` wins over the parameter's own `default`; a parameter with
 * neither is left alone rather than bound to an empty selection, because an
 * empty `in` filter matches nothing and would render an empty dashboard instead
 * of an unfiltered one.
 */
export function applyParameters(
  parameters: readonly DashboardParameter[],
  controls: readonly NativeFilterSpec[],
  values: Readonly<Record<string, string | undefined>>,
  base: FilterState = defaultFilterState(),
): TemplateInstance {
  let filters = base;
  const unbound: string[] = [];
  const unset: string[] = [];

  for (const parameter of parameters) {
    const value = values[parameter.name] ?? parameter.default ?? null;
    if (!value) {
      unset.push(parameter.name);
      continue;
    }
    const control = controls.find((c) => c.id === parameter.filter_id);
    if (!control || !control.target_column) {
      unbound.push(parameter.name);
      continue;
    }
    const column = control.target_column;
    filters = {
      ...filters,
      values: [
        ...(filters.values ?? []).filter((v) => v.column !== column),
        { column, op: "in", value: [value] },
      ],
    };
  }
  return { filters, unbound, unset };
}

/** The slug the Experiment template is authored under
 *  (`superset/dashboards/Experiment_template_29.yaml`).
 *
 *  One constant rather than a search over titles: `/x/<key>` has to resolve to
 *  ONE dashboard, and matching on a title would pick a different board the day
 *  somebody names theirs "Experiment". Where an environment lacks it, the route
 *  renders an empty state naming this slug. */
export const EXPERIMENT_TEMPLATE_SLUG = "experiment";

/** The parameter name the Experiment template binds its arm control to. */
export const EXPERIMENT_PARAMETER = "experiment";
